import type { LineId, NetworkGraph } from '@mrt/graph';
import type { ClientStatus } from '../lib/useLiveState.js';
import { t } from '../lib/i18n.js';
import type { Lang } from '../lib/i18n.js';

export function GlassHeader({
  lang,
  status,
  lastSnapshotAt,
  trains,
  onToggleLang,
}: {
  lang: Lang;
  status: ClientStatus;
  lastSnapshotAt: number | null;
  trains: number;
  onToggleLang: () => void;
}) {
  const s = t(lang);
  const healthy = status === 'live';
  const label =
    status === 'live'
      ? s.live
      : status === 'connecting'
        ? s.connecting
        : status === 'stale'
          ? s.stale
          : status === 'offline'
            ? s.offline
            : s.downShort;

  const age = lastSnapshotAt === null ? null : Math.max(0, Math.round((Date.now() - lastSnapshotAt) / 1000));
  const ageLabel = age === null ? '' : age < 90 ? s.secondsAgo(age) : s.minutesAgo(Math.round(age / 60));

  return (
    <header className="glass bar">
      <div className="bar-left">
        <h1>{s.title}</h1>
        <span className="sub">
          {trains} {s.trains}
        </span>
      </div>
      <div className="bar-right">
        <span className={`status ${healthy ? 'ok' : 'warn'}`}>
          <span className="dot" aria-hidden="true" />
          {label}
        </span>
        {lastSnapshotAt !== null && <span className="age">{ageLabel}</span>}
        <button type="button" className="lang" onClick={onToggleLang} aria-label="Switch language">
          {s.langToggle}
        </button>
      </div>
    </header>
  );
}

/**
 * The single global freshness signal (F5). There are deliberately no per-train
 * confidence badges: every marker is an observed arrival, so qualifying them
 * individually would be noise.
 */
export function StatusBanner({ lang, status, trains }: { lang: Lang; status: ClientStatus; trains: number }) {
  const s = t(lang);
  if (status === 'connecting') return null;
  if (status === 'live') {
    // An empty map at 01:00 is correct, but silence reads as broken — say why.
    if (trains > 0) return null;
    return (
      <div className="glass banner" role="status">
        <strong className="quiet">{s.noService}</strong>
        <span>{s.noServiceHint}</span>
      </div>
    );
  }
  return (
    <div className="glass banner" role="status">
      <strong>{status === 'offline' ? s.offline : status === 'stale' ? s.stale : s.down}</strong>
      <span>{s.downHint}</span>
    </div>
  );
}

export function LineTray({
  graph,
  lang,
  visible,
  onToggle,
  onAll,
}: {
  graph: NetworkGraph;
  lang: Lang;
  visible: Set<LineId>;
  onToggle: (id: LineId) => void;
  onAll: () => void;
}) {
  const s = t(lang);
  const all = visible.size === graph.lines.length;
  return (
    <nav className="glass tray" aria-label={s.allLines}>
      <button type="button" className={`seg ${all ? 'on' : ''}`} onClick={onAll}>
        {s.allLines}
      </button>
      {graph.lines.map((line) => {
        const on = visible.has(line.id);
        return (
          <button
            key={line.id}
            type="button"
            className={`seg ${on ? 'on' : ''}`}
            onClick={() => onToggle(line.id)}
            aria-pressed={on}
          >
            <span className="swatch" style={{ background: line.colorOnDark }} aria-hidden="true" />
            {lang === 'zh' ? line.name.zh : line.name.en}
          </button>
        );
      })}
    </nav>
  );
}
